import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsData {
  connections: {
    instagram: boolean;
    tiktok: boolean;
    youtube: boolean;
  };
  weeklyStats: {
    followers: { current: number; change: number };
    engagement: { current: number; change: number };
    reach: { current: number; change: number };
    likes: { current: number; change: number };
  };
  chartData: Array<{
    date: string;
    followers: number;
    engagement: number;
    reach: number;
  }>;
  lastUpdated: string | null;
}

export const useAnalyticsData = () => {
  const { currentUser } = useUser();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalyticsData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      // Get profile_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profile) {
        setError('No profiles linked');
        return;
      }

      // Query social_connections to determine which platforms are connected
      const { data: connections } = await supabase
        .from('social_connections')
        .select('platform')
        .eq('profile_id', profile.id);

      const connectedPlatforms = new Set((connections || []).map(c => c.platform));

      const connectionsMap = {
        instagram: connectedPlatforms.has('instagram'),
        tiktok: connectedPlatforms.has('tiktok'),
        youtube: connectedPlatforms.has('youtube'),
      };

      // Query latest snapshots per platform (most recent date)
      const { data: latestSnapshots } = await supabase
        .from('social_analytics_snapshots')
        .select('*')
        .eq('profile_id', profile.id)
        .order('snapshot_date', { ascending: false })
        .limit(10);

      // Query snapshots from ~7 days ago for comparison
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekAgoStr = weekAgo.toISOString().split('T')[0];

      const { data: previousSnapshots } = await supabase
        .from('social_analytics_snapshots')
        .select('*')
        .eq('profile_id', profile.id)
        .lte('snapshot_date', weekAgoStr)
        .order('snapshot_date', { ascending: false })
        .limit(10);

      // Get latest snapshot per platform
      const latestByPlatform: Record<string, any> = {};
      for (const snap of (latestSnapshots || [])) {
        if (!latestByPlatform[snap.platform]) {
          latestByPlatform[snap.platform] = snap;
        }
      }

      const previousByPlatform: Record<string, any> = {};
      for (const snap of (previousSnapshots || [])) {
        if (!previousByPlatform[snap.platform]) {
          previousByPlatform[snap.platform] = snap;
        }
      }

      // Aggregate current stats across all platforms
      let totalFollowers = 0;
      let totalReach = 0;
      let totalLikes = 0;
      let engagementSum = 0;
      let engagementCount = 0;

      for (const snap of Object.values(latestByPlatform)) {
        totalFollowers += snap.followers || 0;
        totalReach += snap.reach || 0;
        totalLikes += snap.likes || 0;
        if (snap.engagement_rate > 0) {
          engagementSum += Number(snap.engagement_rate);
          engagementCount++;
        }
      }

      // Aggregate previous stats
      let prevFollowers = 0;
      let prevReach = 0;
      let prevLikes = 0;
      let prevEngagementSum = 0;
      let prevEngagementCount = 0;

      for (const snap of Object.values(previousByPlatform)) {
        prevFollowers += snap.followers || 0;
        prevReach += snap.reach || 0;
        prevLikes += snap.likes || 0;
        if (snap.engagement_rate > 0) {
          prevEngagementSum += Number(snap.engagement_rate);
          prevEngagementCount++;
        }
      }

      const calcChange = (current: number, previous: number) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return Math.round(((current - previous) / previous) * 1000) / 10;
      };

      const currentEngagement = engagementCount > 0 ? engagementSum / engagementCount : 0;
      const prevEngagement = prevEngagementCount > 0 ? prevEngagementSum / prevEngagementCount : 0;

      // Query chart data: last 8 weekly snapshots aggregated by date
      const { data: chartSnapshots } = await supabase
        .from('social_analytics_snapshots')
        .select('snapshot_date, followers, engagement_rate, reach')
        .eq('profile_id', profile.id)
        .order('snapshot_date', { ascending: true });

      // Group by snapshot_date and aggregate
      const dateMap: Record<string, { followers: number; engagement: number; reach: number; engCount: number }> = {};
      for (const snap of (chartSnapshots || [])) {
        const date = snap.snapshot_date;
        if (!dateMap[date]) {
          dateMap[date] = { followers: 0, engagement: 0, reach: 0, engCount: 0 };
        }
        dateMap[date].followers += snap.followers || 0;
        dateMap[date].reach += snap.reach || 0;
        if (snap.engagement_rate > 0) {
          dateMap[date].engagement += Number(snap.engagement_rate);
          dateMap[date].engCount++;
        }
      }

      const chartData = Object.entries(dateMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-8)
        .map(([date, stats]) => ({
          date,
          followers: stats.followers,
          engagement: stats.engCount > 0 ? Math.round((stats.engagement / stats.engCount) * 10) / 10 : 0,
          reach: stats.reach,
        }));

      // Determine lastUpdated from the most recent snapshot
      const lastSnapshotDate = latestSnapshots?.[0]?.created_at || null;

      setAnalyticsData({
        connections: connectionsMap,
        weeklyStats: {
          followers: { current: totalFollowers, change: calcChange(totalFollowers, prevFollowers) },
          engagement: { current: currentEngagement, change: calcChange(currentEngagement, prevEngagement) },
          reach: { current: totalReach, change: calcChange(totalReach, prevReach) },
          likes: { current: totalLikes, change: calcChange(totalLikes, prevLikes) },
        },
        chartData,
        lastUpdated: lastSnapshotDate,
      });
    } catch (err) {
      setError('Failed to load analytics data');
      console.error('Analytics data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const refreshData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      // Call edge function to fetch fresh data from social APIs
      const { error: fnError } = await supabase.functions.invoke('fetch-social-analytics');

      if (fnError) {
        console.error('Refresh error:', fnError);
      }

      // Re-query the database for updated snapshots
      await fetchAnalyticsData();
    } catch (err) {
      console.error('Refresh error:', err);
      setLoading(false);
    }
  }, [currentUser, fetchAnalyticsData]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  return {
    analyticsData,
    loading,
    error,
    refreshData,
  };
};
